import React, { useCallback, useEffect, useState } from "react";

import GoogleMapReact from "google-map-react";

import {
  formatNumber,
  getDefaultSquare,
  getGoogleKey,
  getZoomLevel,
  getZoomLevelMultipleFields,
  matchGeoFirstAndLastCoords,
  updateMapFooterStyles,
  useCookieConsent,
} from "utils";

import { MapCookieConsentReminder, MapMarker } from "components";

export const MapInteractive = ({
  defaultZoom, // overrides default if defined
  disableDrag, // sets the drag option to false
  editPolygon, // makes polygons on map editable
  field, // should be used for maps with single shape
  fields, // should be used for maps with multiple shapes
  fieldSize,
  geometry, // should be used for maps with single shape
  geometries, // should be used for maps with multiple shapes && shape splitting
  markers,
  location,
  onChange,
  scaleControl = true, // sets scale control to true
  setAreas, // controls state of edit field shape
  setFieldSize, // "
  setLocation, // "
  setShape, // "
  setShapeEdited, // "
  setupDrawingAPI, // function to set up drawing API if needed
  split, // if true, map will be set up to split polygons
  updateArea, // function to execute if field size changes (currently displays areas in admin)
  zoomControl = true, // sets zoom control to true
}) => {
  const [map, setMap] = useState(null);
  const [maps, setMaps] = useState(null);
  const [zoom, setZoom] = useState(15);
  const [coordinates, setCoordinates] = useState({
    lat: 52.5224832,
    lng: 13.3906564,
    default: true,
  });
  const hasCookieConsent = useCookieConsent();

  const getMapOptions = (maps) => ({
    streetViewControl: false,
    keyboardShortcuts: false,
    scaleControl: scaleControl,
    fullscreenControl: false,
    draggable: !disableDrag,
    rotateControl: false,
    styles: [
      {
        featureType: "poi.business",
        elementType: "labels",
        stylers: [
          {
            visibility: "off",
          },
        ],
      },
    ],
    gestureHandling: "cooperative",
    disableDoubleClickZoom: true,
    mapTypeControl: false,
    mapTypeId: maps.MapTypeId.HYBRID,
    mapTypeControlOptions: {
      style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
      position: maps.ControlPosition.BOTTOM_CENTER,
      mapTypeIds: [maps.MapTypeId.ROADMAP, maps.MapTypeId.SATELLITE, maps.MapTypeId.HYBRID],
    },
    zoomControl: zoomControl,
    clickableIcons: false,
  });

  const getStartingZoom = useCallback(() => {
    if (defaultZoom) {
      setZoom(defaultZoom);
    } else if (fields && fields.length > 0) {
      setZoom(getZoomLevelMultipleFields(fields));
    } else if (field) {
      setZoom(getZoomLevel(field));
    }
  }, [defaultZoom, field, fields]);

  const getPolygonSize = (poly) => (maps && maps.geometry ? Math.round(maps.geometry.spherical.computeArea(poly.getPath()) / 100) / 100 : 0);

  const getPolygonGeometry = (poly) => {
    const coordinateArray = poly.getPath().getArray();
    const coordinates = coordinateArray.map((item) => ({
      lat: item.lat(),
      lng: item.lng(),
    }));
    return matchGeoFirstAndLastCoords(coordinates);
  };

  const getPolygonCenter = (poly, maps) => {
    const bounds = new maps.LatLngBounds();
    const geom = getPolygonGeometry(poly);
    for (let i = 0; i < geom.length; i += 1) {
      bounds.extend(geom[i]);
    }
    const center = {
      lat: bounds.getCenter().lat(),
      lng: bounds.getCenter().lng(),
    };
    setLocation(center);
    return center;
  };

  const polygonStyling = (data) => ({
    strokeColor: "#ffffff",
    strokeOpacity: data.strokeOpacity ? data.strokeOpacity : 0.8,
    strokeWeight: data.strokeWeight ? data.strokeWeight : 2,
    fillColor: data.red ? "#db3804" : "#ffffff",
    fillOpacity: data.fillOpacity ? data.fillOpacity : 0.2,
    paths: data.path,
    clickable: !!editPolygon,
    editable: editPolygon,
    draggable: false,
    geodesic: editPolygon,
    map: data.map,
  });

  const generateShape = (polygon) =>
    new maps.Polygon(
      polygonStyling({
        map: map,
        path: polygon.geometry,
      })
    );

  const generateShapes = (polygons) =>
    polygons.map((polygon) => {
      const field = fields && fields.length && fields.find((field) => field.id === polygon.fieldId);
      return new maps.Polygon(
        polygonStyling({
          map: map,
          path: split ? polygon : matchGeoFirstAndLastCoords(polygon.geometry),
          red: !split && (!field || !field.reviewed),
          fillOpacity: polygon.fillOpacity,
          strokeOpacity: polygon.strokeOpacity,
          strokeWeight: polygon.strokeWeight,
        })
      );
    });

  const getUpdatedShape = (poly, maps, currentGeometry) => {
    const updatedGeometry = getPolygonGeometry(poly);
    const center = getPolygonCenter(poly, maps);

    // If currentGeometry is a full FieldShape object (has fieldId, center, type), preserve those properties
    if (currentGeometry && typeof currentGeometry === "object" && "fieldId" in currentGeometry && "type" in currentGeometry) {
      return {
        ...currentGeometry,
        geometry: updatedGeometry,
        center: center,
      };
    }

    // Otherwise, return just the geometry array for backward compatibility
    return updatedGeometry;
  };

  const addEditEventListeners = (poly, currentGeometry) => {
    maps.event.addListener(map, "mouseup", () => {
      getPolygonCenter(poly, maps);
    });

    maps.event.addListener(poly, "mouseup", () => {
      const updatedShape = getUpdatedShape(poly, maps, currentGeometry);
      setFieldSize(getPolygonSize(poly));
      setShapeEdited(true);
      setShape(updatedShape);
    });

    maps.event.addListener(poly, "rightclick", (d) => d.vertex && poly.getPath().removeAt(d.vertex));
    maps.event.addListener(poly, "dblclick", (d) => d.vertex && poly.getPath().removeAt(d.vertex));

    const updatedShape = getUpdatedShape(poly, maps, currentGeometry);
    setShape(updatedShape);
    setTimeout(() => {
      setFieldSize(getPolygonSize(poly));
    }, 300);
  };

  const addGeneralEventListeners = () => {
    maps.event.addListener(map, "idle", () => {
      updateMapFooterStyles();
    });
  };

  const setupSplitMap = () => {
    const shapes = generateShapes(geometries);
    const newAreas = [];

    if (shapes && shapes.length) {
      shapes.map(async (shape) => {
        const bounds = new maps.LatLngBounds();
        const geometry = shape.getPath().getArray();

        for (let i = 0; i < geometry.length; i += 1) {
          bounds.extend(geometry[i]);
        }

        const Geometry = await maps.importLibrary("geometry");

        const area = Math.round(Geometry.spherical.computeArea(shape.getPath()) / 100) / 100;

        return newAreas.push({
          area: area,
          center: {
            lat: bounds.getCenter().lat(),
            lng: bounds.getCenter().lng(),
          },
          geometry: geometry,
        });
      });

      setAreas(newAreas);
    }
  };

  const generateMarkers = () =>
    markers.map((marker, i) => (
      <MapMarker
        className={marker.unreviewed ? "unreviewed" : "reviewed"}
        key={i}
        lat={marker.location.lat}
        link={marker.link && marker.id && `/farm/field/${marker.id}`}
        lng={marker.location.lng}
        text={marker.name && marker.name}
        size={marker.size ? marker.size : "l"}
        fieldSize={
          marker.fieldSize &&
          `${formatNumber({
            number: split ? marker.fieldSize : fieldSize,
            decimalPlaces: 2,
          })} ha`
        }
      />
    ));

  useEffect(() => {
    if (map && maps) {
      // add general listeners for all maps
      addGeneralEventListeners();

      // initiates drawing API for all editable maps
      if (setupDrawingAPI) {
        setupDrawingAPI(map, maps, field);
      }

      // splitting map requires some extra setup
      if (split) {
        setupSplitMap();
      }

      // setup for maps with multiple shapes
      if (!split && geometries && geometries.length > 0) {
        generateShapes(geometries);
      }

      // setup for maps with a single shape
      if (geometry) {
        const square = location ? getDefaultSquare(location) : [];
        const geometries = geometry && geometry.geometry ? { geometry: geometry.geometry } : { geometry: square };
        const poly = generateShape(geometries);

        // adds event listeners if there is edit functionality for the polygon - only occurs on maps with single shape
        if (poly && editPolygon) {
          addEditEventListeners(poly, geometry);
        }

        // handles updating the size of each type of polygon on the admin farm fields tab
        if (updateArea && maps.geometry) {
          const area = getPolygonSize(poly);
          updateArea(area);
        }

        // return poly;
      }
    }
  }, [map, maps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!coordinates || coordinates.default) {
      setCoordinates(geometry?.center || field?.location || location);
    }

    getStartingZoom();
  }, [field?.location, fields, geometry?.center, getStartingZoom, geometries, location, coordinates]);

  if (!hasCookieConsent) {
    return <MapCookieConsentReminder />;
  }

  return (
    <GoogleMapReact
      key={`${coordinates.lng.toFixed(1)}-${coordinates.lat.toFixed(1)}`}
      bootstrapURLKeys={{
        key: getGoogleKey(),
        libraries: ["drawing"],
      }}
      defaultCenter={{ lat: 52.52437, lng: 13.41053 }}
      center={coordinates}
      zoom={zoom}
      defaultZoom={15}
      draggable={split}
      options={(maps) => getMapOptions(maps)}
      yesIWantToUseGoogleMapApiInternals={true}
      onChange={onChange ? (v) => onChange(v) : null}
      onGoogleApiLoaded={({ map, maps }) => {
        // set map and maps globally for ease
        // use hooks to do things with them
        setMap(map);
        setMaps(maps);
      }}
    >
      {markers && markers.length && generateMarkers(markers)}
    </GoogleMapReact>
  );
};

export default MapInteractive;
